/**
 * 页面任务键的**旧名归一** —— 单一真源。
 *
 * ===========================================================================
 * 为什么需要这张表
 * ===========================================================================
 *
 * 右栏「本页历史」的取数方式是 `jobsFor(页面键)`，而页面键被写进了两处：
 *   · 提交时落库的 `sourcePage`（后端任务表里的一列，**写完就改不了**）
 *   · 页面上「本页」的取数键（跟着页面走，**每次合并都会变**）
 *
 * 后端 `app/taxonomy.py` 已经把这个现象写在文件头了：
 * 「页面 slug 会漂（历史数据里同一个功能出现过 `banner`、`professional/fashion`、
 *  `fashion-model-gen` 三种写法），但 app_slug 不会。」
 *
 * 页面合并（2026-09-28 ~ 09-29 连续做了五次）之后，"旧写法"的那批任务在
 * 新页面上**永远查不出来** —— 右栏不报错、不警告，历史就是空的。
 * 表现是「我刚生成的怎么不见了」，用户只会以为自己看错了页。
 *
 * 典型损失（改这张表前的实测数据，共 43 条任务）：
 *   `ai-set`(5) + `detail-main`(2)  →  合并后归 `/detail-page`，查不出
 *   `fashion-model-gen`(6)          →  归 `/professional/fashion`，查不出
 *
 * ===========================================================================
 * 规则
 * ===========================================================================
 *
 * 1. **只收「本项目自己声明过的旧名字」** —— 依据必须是代码里已有的事实：
 *    `App.tsx` 的路由别名（`/ai-set` `/localize` `/main-video` `/short-video`）、
 *    或模板中心的深链（`/professional/fashion?feature=model-gen` 证明
 *    `fashion-model-gen` 是服饰图旧键）。**不许凭印象补一个「大概是这样」的键** ——
 *    映射错了会把别的功能的记录倒进这一页，比查不出来更糟。
 * 2. **值是「现在的页面键」**，不是别的旧名。一个旧名只准映射一次（`_v` 后缀的
 *    中间态不进来）。
 * 3. 表里**不许出现当前仍在用的键作为 key**（比如 `detail-page` → 别的页），
 *    那会把现役记录倒走。冒烟测试守着这条。
 * 4. 新增页面合并时**必须在同一轮补这张表**，并加断言 —— 否则又是一轮静默丢失。
 *
 * 为什么放在 `api` 之外的 `config`：它是**页面身份**的一部分，与网络无关；
 * `JobCenterContext` 只是消费方（`jobsFor` / `outputsFor` 两个入口都过它），
 * 页面自己不许再抄一份判断。
 */
export const PAGE_KEY_ALIASES: Record<string, string> = {
  /* -------- 合并到 /detail-page（主图 · 详情） -------- */
  // `/localize` → `/detail-page`（App.tsx 路由别名；原本直达的「成片派生多语言版」
  // 已于 2026-09-29 整块下架，别名不再带 `?mode=`，只保证老书签不掉进 `*` 兜底）
  localize: 'detail-page',
  // `/ai-set` → `/detail-page`（App.tsx 路由别名）。**不带 `?mode=set` 了**：
  // 「商品套图」形态 2026-09-30 下架（用户原话「这套图跟详情页重复了」），
  // 别名与 `/localize` 同类 —— 只保证老书签不掉进 `*` 兜底，落回默认形态。
  'ai-set': 'detail-page',
  // 合并前「主图与详情」与「商品套图」同在一个 /detail-page 路由下，
  // 各自写自己的页面键；合并后统一写 `detail-page`
  'detail-main': 'detail-page',

  /* -------- 合并到 /video-studio（带货短视频 · 商品视频） -------- */
  // `/main-video`、`/short-video` → `/video-studio?mode=...`（App.tsx 路由别名）。
  // ⚠️ 这两条**并档后也不能删**：并掉的是**档位**（页面顶部那排 chip），
  // 不是历史数据的页面键 —— 库里旧任务的 `source_page` 写的就是这两个值，
  // 删了它们那批记录会从右栏「本页历史」里整批消失。
  'main-video': 'video-studio',
  'short-video': 'video-studio',

  /* -------- 服饰图：页面键曾用 app_slug 本身 -------- */
  // 依据：模板中心 `st-03` 深链是 `/professional/fashion?feature=model-gen`，
  // 所以 `fashion-model-gen` 就是服饰图这一页的旧键
  'fashion-model-gen': 'professional/fashion',
}

/**
 * 把（可能是旧名的）页面键折算成现在的页面键。
 *
 * **读的时候必须两边都折算**：调用方传进来的键、任务自己带的 `sourcePage`
 * 都可能有一头是旧的 —— 只折算一边，剩下的那一半照样对不上。
 * 现役键过一遍没有副作用（表里没有以现役键为 key 的项，见规则 3）。
 */
export const canonPageKey = (key: string): string => PAGE_KEY_ALIASES[key] ?? key
